import { useCallback } from 'react';

import { useBridgewayApi } from '@/api/BridgewayApi';
import { RestApiError } from '@/api/RestApi';
import { useUserId } from '@/auth/UserIdContext';
import { logger } from '@/logger';

import type { ModelChoice } from '../../../../bridge/types/strategy.ts';

// Map the generate route's HTTP codes to calm, owner-facing copy (no leaked status
// or backend message); any other failure gets a generic line.
// Two routes share this function: generate and regenerate. Generate's only 409 is the
// pattern-corpus guard — nothing the owner can supply fixes it, so the copy must not send
// them to Brand to add business detail they already have. Regenerate adds a 409 that is
// healthy behaviour, so it needs its own branch (see the 409 case).
export function generateErrorCopy(
	status: number | undefined,
	detail?: string,
): string {
	switch (status) {
		// The route's one-at-a-time guard (`_assert_capacity`) is the only 429 the generate
		// route itself raises, and it says so in `detail`. Name that reason only when the
		// backend does. Any other 429 — an edge throttle, or an api that still enforces the
		// removed daily cap — falls through to the generic line. Matched loosely, like the
		// delete 409s below, so a backend wording tweak degrades the copy to vague, never to
		// wrong. It must never mention a daily quota: there is none.
		case 429:
			if ((detail ?? '').toLowerCase().includes('already in progress'))
				return 'A strategy is already being built for this business. Please wait for it to finish.';
			break;
		case 409: {
			const d = (detail ?? '').toLowerCase();
			// The regenerate route refuses a run it CANNOT PROVE has stopped. Its `_is_retryable`
			// gate fails closed on purpose, so this one detail covers three different runs: one
			// that is genuinely still building, one whose graph died silently, and one that
			// already finished. The copy therefore states no fact about what the server is
			// doing, because in two of the three that fact would be false.
			//
			// It also must not tell the owner to stop pressing. The watchdog reads a run as
			// failed at 45 minutes while its graph runs to 50, so this refusal lands under a
			// banner that says "Run failed". Pressing again after that deadline is the ONE
			// action that frees a silently dead run.
			//
			// The refusal is still healthy, so the copy does not blame us. Matched on two
			// anchors, like the delete 409s below. One word alone would also catch a future
			// 409 that merely mentions a rebuild, and the whole phrase would break on any
			// reword, so a re-worded detail degrades to the generic 409 line instead of to a
			// wrong one.
			if (d.includes('failed strategy') && d.includes('rebuilt'))
				return 'This strategy isn’t ready to rebuild yet — it may still be finishing. Please try again in a few minutes.';
			// Generate's pattern-corpus guard, and every other 409 (including the etag race,
			// where "try again later" is the right advice).
			return 'Strategy building is unavailable right now. This one is on us — please try again later.';
		}
		case 503:
			return 'The strategist is busy right now. Please try again in a few minutes.';
	}
	return "We couldn't start your strategy. Please try again.";
}

// Owner-facing copy for the per-campaign BUILD POST. A 409 means another in-flight build of
// this campaign won the CAS (not a failure to retry), so it gets distinct copy.
export function materializeCampaignErrorCopy(
	status: number | undefined,
): string {
	switch (status) {
		case 409:
			return 'This campaign is already being set up — it’ll appear here shortly.';
		default:
			return "We couldn't build this campaign. Please try again.";
	}
}

// Owner-facing copy for the delete DELETE. The backend 409s for four distinct, actionable
// reasons (active campaign, still generating, mid-activation, or a concurrent change lost
// the etag race). Each carries the reason in `detail`; we map to copy that tells the owner
// what to do rather than a dead-end "try again" (retrying a 409 is futile until the blocker
// clears). The reason text is matched loosely so a backend wording tweak degrades to the
// generic 409 line, never a wrong one.
export function deleteErrorCopy(
	status: number | undefined,
	detail?: string,
): string {
	if (status === 409) {
		const d = (detail ?? '').toLowerCase();
		// Order matters: an ACTIVATED campaign (deactivate it) vs one mid-ACTIVATION (wait).
		if (d.includes('active campaign'))
			return 'Deactivate this strategy’s campaign before deleting it.';
		if (d.includes('materializing'))
			return 'A campaign is being set up right now — try deleting once it finishes.';
		if (d.includes('generating'))
			return 'This strategy is still generating — try deleting once it finishes.';
		// Terminal etag-race 409 ("while it is changing") and any other 409.
		return 'This strategy is changing right now — try again in a moment.';
	}
	return "We couldn't delete this strategy. Please try again.";
}

// Thrown by remove() so a delete control can show the specific, actionable owner-facing
// message (409 reasons) instead of a fixed generic line. `.message` is the mapped copy.
export class DeleteError extends Error {
	status: number | undefined;
	constructor(status: number | undefined, detail?: string) {
		super(deleteErrorCopy(status, detail));
		this.name = 'DeleteError';
		this.status = status;
	}
}

// Owner-facing copy for the run-level activate/deactivate POSTs. A 409 means the run is no
// longer in the state the press assumed (someone else flipped it, or a concurrent write won
// the CAS) — the fix is to look again, not to retry blindly.
export type StrategyLifecycleAction = 'activate' | 'deactivate' | 'acknowledge';

export function strategyLifecycleErrorCopy(
	status: number | undefined,
	action: StrategyLifecycleAction,
): string {
	if (status === 409)
		return 'This strategy just changed — refresh to see where it stands.';
	if (action === 'acknowledge')
		return "We couldn't open this request. Please try again.";
	return action === 'activate'
		? "We couldn't start this strategy. Please try again."
		: "We couldn't stop this strategy. Please try again.";
}

// Thrown by the run-level activate()/deactivate() calls so a control can branch on the HTTP
// status without parsing copy. Carries the mapped owner-facing message as `.message`.
export class StrategyLifecycleError extends Error {
	status: number | undefined;
	constructor(status: number | undefined, action: StrategyLifecycleAction) {
		super(strategyLifecycleErrorCopy(status, action));
		this.name = 'StrategyLifecycleError';
		this.status = status;
	}
}

// Thrown by materializeCampaign() so the control can branch on the HTTP status (409 vs
// generic) without parsing copy. Carries the mapped owner-facing message as `.message`.
export class MaterializeCampaignError extends Error {
	status: number | undefined;
	constructor(status: number | undefined) {
		super(materializeCampaignErrorCopy(status));
		this.name = 'MaterializeCampaignError';
		this.status = status;
	}
}

export interface GenerateArgs {
	businessId: string;
	modelChoice?: ModelChoice;
}

/**
 * What `POST /strategies/{id}/campaigns/{cid}/materialize` answers.
 *
 * `status` is the campaign's OWN terminal status, and the two values mean different
 * things: `materialized` is a Process built for the owner to read, sitting in
 * `proposed` with nothing running; `activated` is one they already said yes to.
 * Re-entry on a built campaign short-circuits and returns whichever it is, so a
 * caller that reads only `process_id` cannot tell them apart — and used to report
 * both as "Activated".
 *
 * Optional because a `status`-less reply from an older backend should degrade to
 * "built", which is the truthful floor: the route did build something.
 */
export interface MaterializeCampaignReply {
	process_id: string;
	campaign_id?: string;
	/**
	 * `superseded` means the owner's plan moved while this build was running, so the route threw
	 * the build away rather than store content made from the old plan. It carries NO
	 * `process_id`: there is nothing to open. Modelling it matters because an unmodelled status
	 * falls through to `not_built`, which renders nothing at all.
	 */
	status?: 'materializing' | 'materialized' | 'activated' | 'superseded';
}

/**
 * What `POST /strategies/{id}/materialize` reports.
 *
 * The route builds every campaign on the run concurrently and answers **200 either way** —
 * keyed by campaign id into `materialized` for the ones that built and `failed` for the ones
 * that did not. That is deliberate for partial failure: a strategy where five of six
 * campaigns built is still worth reading, and the sixth retries on the next open.
 *
 * The consequence is that the status line carries no information. A run that built nothing is
 * indistinguishable from a complete success unless you read this body, which is why
 * `materialize` returns it instead of discarding it.
 */
export interface MaterializeReport {
	materialized: Record<string, unknown>;
	/** Campaign id -> the reason it was refused. */
	failed: Record<string, string>;
}

/** True when the route built nothing AND said why — the case the owner must be told about. */
export function materializeBuiltNothing(
	report: MaterializeReport | null,
): boolean {
	if (!report) return false;
	return (
		Object.keys(report.materialized ?? {}).length === 0 &&
		Object.keys(report.failed ?? {}).length > 0
	);
}

const PLAN_EDIT_FAILED = "We couldn't save that change. Please try again.";

const PLAN_EDIT_LOCKED =
	'This strategy is running or being set up, so its plan is locked. Stop it first to make changes.';

// The plan route refuses an edit for reasons the OWNER can act on, and it writes each one in its
// own `detail`. The copy read `status` alone, so every 422 became "That change wasn't something we
// could save" and the owner was refused with no reason at all (ADO-1984).
//
// Matched on a distinctive fragment, never on the whole sentence, and never printed raw: a backend
// wording tweak then degrades the copy to the generic line rather than to a wrong one. This is the
// same rule `generateErrorCopy` and `deleteErrorCopy` already follow.
const PLAN_EDIT_REASONS: [RegExp, string][] = [
	[
		/never a rate|per-period/i,
		'Success has to be one total you reach once and are done — not a rate, a ratio, a percentage or a per-period measure.',
	],
	[/more than zero/i, 'The number to reach has to be more than zero.'],
	[/baseline cannot be negative/i, 'Where you are now cannot be below zero.'],
	[
		/below where you are going/i,
		'Where you are now has to be below the number you are going for.',
	],
	[/needs a name/i, 'Success needs a name for the thing it counts.'],
	[/needs a purpose/i, 'Success needs a reason it is the number to watch.'],
	[
		/plain business descriptor|cannot say 'competitor' or 'baseline'/i,
		'That wording cannot mention a competitor or a baseline.',
	],
	// A campaign line, not a metric name, so it is a different rule with a different remedy: R17
	// bans the word in plan prose and the route names the concrete alternative to write instead.
	// BELOW the metric row on purpose. Both details carry "cannot say 'competitor'", and the metric
	// row is matched on "plain business descriptor", so putting this one first would answer a
	// metric refusal with a remedy that does not apply to it.
	[
		/cannot say 'competitor'/i,
		'That wording cannot mention a competitor. Name the concrete alternative instead — a DIY spreadsheet, the big chain, or doing nothing.',
	],
];

/** Owner-facing copy per outcome. The route's own reasons are the ones worth repeating: a running
 *  strategy is locked, a strategy mid-setup is busy, and a refused edit says what would fix it.
 *  Everything else gets one plain sentence. */
export function planEditErrorCopy(status?: number, detail?: string): string {
	const reason = detail ?? '';
	if (status === 409) {
		// Three different 409s, and only two of them are locks. The third is a lost CAS race on a
		// strategy that is `complete`: nothing is running, so "stop it first" names a control the
		// owner does not have. It is a retry, and it has to read as one.
		if (/changing right now|try again in a moment/i.test(reason))
			return 'This plan is being changed right now. Please try again in a moment.';
		return PLAN_EDIT_LOCKED;
	}
	if (status === 404) return 'That part of the plan is no longer there.';
	if (status === 422) {
		for (const [pattern, copy] of PLAN_EDIT_REASONS) {
			if (pattern.test(reason)) return copy;
		}
		return "That change wasn't something we could save.";
	}
	return PLAN_EDIT_FAILED;
}

/** One change to a plan. `op` decides which other fields the route reads; it ignores the rest. */
export type PlanEdit =
	| { op: 'set_wish'; outcome: string }
	| { op: 'set_audience_who'; who: string }
	// `metric` names the metric by id or by name. Omit it and the route takes the primary one.
	| { op: 'set_metric_target'; target: number; metric?: string }
	// The whole success definition, not only its number. Every field is optional and the route
	// reads only the ones sent, but it refuses an edit that sends none.
	| {
			op: 'set_metric';
			metric?: string;
			name?: string;
			unit?: string;
			purpose?: string;
			target?: number;
			baseline?: number;
	  }
	// A campaign is addressed by id ONLY. There is no positional fallback, on purpose.
	| { op: 'set_campaign_aim'; campaign_id: string; aim: string }
	// The whole campaign, the way `set_metric` is the whole success definition. Its steps and
	// triggers are deliberately absent: they drive how Bridgeway builds the campaign.
	| {
			op: 'set_campaign';
			campaign_id: string;
			title?: string;
			aim?: string;
			what_you_do?: string;
			report_back?: string;
	  }
	| { op: 'drop_campaign'; campaign_id: string };

export interface UseStrategyActions {
	// Fans out 3 variants; resolves to the batchId on success or throws the copy.
	generate: (args: GenerateArgs) => Promise<string>;
	// Rebuild ONE dead variant in place, keeping its id, tier and batch. Distinct from
	// `generate`, which starts a fresh batch of three — pressing retry on one failed variant
	// used to rebuild all three. Throws the same owner-facing copy `generate` throws, so the
	// one-at-a-time 429 reads the same wherever it is shown.
	regenerate: (strategyId: string) => Promise<void>;
	remove: (id: string) => Promise<void>;
	// Build ONE campaign; resolves to the route's whole reply. The Process it makes sits in
	// `proposed` and the campaign lands on `materialized` — starting it is the owner's separate
	// yes (see the accept flow). Was `activateCampaign` against a route ending `/activate`,
	// which is the naming ADO-1837 4.2.1 asked to retire: nothing here starts anything.
	//
	// The reply carries `status`, and callers must read it rather than assume: re-entry on an
	// already-`activated` campaign returns that, and treating every success as the same
	// outcome is how the card came to announce "Activated" for work that started nothing.
	//
	// Distinct from `materialize` below, which builds EVERY campaign on the run. They differ
	// by one path segment, so prefer these function names over the paths in logs and tickets.
	materializeCampaign: (
		strategyId: string,
		campaignId: string,
	) => Promise<MaterializeCampaignReply>;
	// Build every campaign on a run. No longer called by the review panel on mount — campaigns
	// are now built on demand via `materializeCampaign`, or at strategy activation time.
	// Safe to call repeatedly, since each campaign's marker short-circuits.
	//
	// Resolves to the route's own report of what happened, or null if the request itself
	// failed. It answers 200 whether it built everything or nothing, so the split is the only
	// way to tell — see MaterializeReport.
	materialize: (strategyId: string) => Promise<MaterializeReport | null>;
	// RUN-level activation: starts the strategy's daily loop (status -> active).
	activate: (strategyId: string) => Promise<void>;
	// RUN-level deactivation: stops the loop and stamps deactivated_at (status -> complete).
	deactivate: (strategyId: string) => Promise<void>;
	// Clear ONE attention_flags item (owner has seen this request). `flagId` is required:
	// posting without it used to stamp every unacknowledged request on the strategy.
	acknowledge: (strategyId: string, flagId: string) => Promise<void>;
	// Change the plan itself, on the owner's behalf. The route takes a LIST so several changes
	// land in one CAS write; it applies them in order and answers with the sentences it applied.
	//
	// Only five ops exist, and the route refuses anything else twice over. Each one maps onto a
	// part of the guided walk: `set_wish` and `set_metric_target` on the idea,
	// `set_audience_who` on the audience, `set_campaign_aim` and `drop_campaign` on a campaign.
	//
	// The run must be `complete`. A running strategy answers 409, because the daily agent reads
	// the plan every cycle and must never find it changed underneath. Callers surface the copy.
	//
	// An edit DROPS the plan's own citation for the field it touched, and `set_metric_target`
	// also drops the metric's `basis`. That is deliberate: the evidence backed the old value.
	editPlan: (strategyId: string, edits: PlanEdit[]) => Promise<string[]>;
}

export function useStrategyActions(): UseStrategyActions {
	const userId = useUserId();
	const bridgewayApi = useBridgewayApi();

	const generate = useCallback(
		async ({ businessId, modelChoice }: GenerateArgs) => {
			if (!userId) throw new Error(generateErrorCopy(undefined));
			try {
				const res = await bridgewayApi.post<{ batchId: string }>(
					`/api/user/${userId}/strategies/generate`,
					{
						businessId,
						...(modelChoice ? { modelChoice } : {}),
					},
				);
				return res.batchId;
			} catch (err) {
				logger.error('Failed to generate strategy', err);
				const status = err instanceof RestApiError ? err.status : undefined;
				const detail =
					err instanceof RestApiError ? err.responseText : undefined;
				throw new Error(generateErrorCopy(status, detail));
			}
		},
		[userId, bridgewayApi],
	);

	const regenerate = useCallback(
		async (strategyId: string) => {
			if (!userId) throw new Error(generateErrorCopy(undefined));
			try {
				await bridgewayApi.post(
					`/api/user/${userId}/strategies/${strategyId}/regenerate`,
					{},
				);
			} catch (err) {
				logger.error('Failed to regenerate strategy', err);
				const status = err instanceof RestApiError ? err.status : undefined;
				const detail =
					err instanceof RestApiError ? err.responseText : undefined;
				throw new Error(generateErrorCopy(status, detail));
			}
		},
		[userId, bridgewayApi],
	);

	const remove = useCallback(
		async (id: string) => {
			if (!userId) return;
			try {
				await bridgewayApi.delete(`/api/user/${userId}/strategies/${id}`);
			} catch (err) {
				logger.error('Failed to delete strategy', err);
				const status = err instanceof RestApiError ? err.status : undefined;
				const detail =
					err instanceof RestApiError ? err.responseText : undefined;
				throw new DeleteError(status, detail);
			}
		},
		[userId, bridgewayApi],
	);

	const materializeCampaign = useCallback(
		async (strategyId: string, campaignId: string) => {
			if (!userId) throw new Error('Not signed in');
			try {
				const res = await bridgewayApi.post<MaterializeCampaignReply>(
					`/api/user/${userId}/strategies/${strategyId}/campaigns/${campaignId}/materialize`,
					{},
				);
				// The whole reply, not just the id. The route short-circuits a campaign
				// that is already built and answers with ITS status, which is the only way
				// to tell "I just built this" from "this was already live" — and dropping
				// it is what let the card report a freshly built campaign as activated.
				return res;
			} catch (err) {
				logger.error('Failed to build campaign', err);
				const status = err instanceof RestApiError ? err.status : undefined;
				throw new MaterializeCampaignError(status);
			}
		},
		[userId, bridgewayApi],
	);

	// Fired when the owner opens a strategy, with no gesture of their own behind it — so it
	// never throws. A strategy that cannot build its campaigns is still one the owner should
	// be able to read; the abstract plan renders regardless, and the next open retries.
	const materialize = useCallback(
		async (strategyId: string): Promise<MaterializeReport | null> => {
			if (!userId) return null;
			try {
				// The reply is the only place a total failure is visible; the status is 200
				// regardless. Returned rather than dropped so the panel can say so.
				return await bridgewayApi.post<MaterializeReport>(
					`/api/user/${userId}/strategies/${strategyId}/materialize`,
					{},
				);
			} catch (err) {
				logger.error('Failed to materialize strategy campaigns', err);
				return null;
			}
		},
		[userId, bridgewayApi],
	);

	// Run-level activation/deactivation. Both surface owner-facing copy on failure; a 409
	// means the run left the state this call expected (someone else already flipped it), so
	// the caller refreshes rather than retrying.
	const activate = useCallback(
		async (strategyId: string) => {
			if (!userId) throw new Error('Not signed in');
			try {
				await bridgewayApi.post(
					`/api/user/${userId}/agents/activate-strategy`,
					{
						strategy_id: strategyId,
					},
				);
			} catch (err) {
				logger.error('Failed to activate strategy', err);
				const status = err instanceof RestApiError ? err.status : undefined;
				throw new StrategyLifecycleError(status, 'activate');
			}
		},
		[userId, bridgewayApi],
	);

	const deactivate = useCallback(
		async (strategyId: string) => {
			if (!userId) throw new Error('Not signed in');
			try {
				await bridgewayApi.post(
					`/api/user/${userId}/agents/deactivate-strategy`,
					{ strategy_id: strategyId },
				);
			} catch (err) {
				logger.error('Failed to deactivate strategy', err);
				const status = err instanceof RestApiError ? err.status : undefined;
				throw new StrategyLifecycleError(status, 'deactivate');
			}
		},
		[userId, bridgewayApi],
	);

	// `flagId` is REQUIRED. Posting without it used to stamp every unacknowledged request on the
	// strategy, so reading one request silently marked the rest read.
	const acknowledge = useCallback(
		async (strategyId: string, flagId: string) => {
			if (!userId || !flagId) return;
			try {
				await bridgewayApi.post(
					`/api/user/${userId}/strategies/${strategyId}/acknowledge`,
					{ flag_id: flagId },
				);
			} catch (err) {
				logger.error('Failed to acknowledge strategy', err);
				// Rethrow like every other action here. Swallowing it left the card looking
				// read while the server still had it unread, so the owner lost the request.
				const status = err instanceof RestApiError ? err.status : undefined;
				throw new StrategyLifecycleError(status, 'acknowledge');
			}
		},
		[userId, bridgewayApi],
	);

	const editPlan = useCallback(
		async (strategyId: string, edits: PlanEdit[]) => {
			if (!userId) throw new Error(PLAN_EDIT_FAILED);
			if (edits.length === 0) return [];
			try {
				const res = await bridgewayApi.patch<{ applied?: string[] }>(
					`/api/user/${userId}/strategies/${strategyId}/plan`,
					{ edits },
				);
				return res.applied ?? [];
			} catch (err) {
				logger.error('Failed to edit strategy plan', err);
				const status = err instanceof RestApiError ? err.status : undefined;
				// The route's own `detail` rides along, the way `generate` already carries it.
				// Without it the copy can only guess from the status code.
				const detail =
					err instanceof RestApiError ? err.responseText : undefined;
				throw new Error(planEditErrorCopy(status, detail));
			}
		},
		[userId, bridgewayApi],
	);

	return {
		generate,
		regenerate,
		remove,
		materializeCampaign,
		materialize,
		activate,
		deactivate,
		acknowledge,
		editPlan,
	};
}
